import {
  Badge,
  Button,
  Dropdown,
  Empty,
  Select,
  Space,
  Typography,
  notification as toast,
} from "antd"
import Layout from "modules/layout/dashboard"

import styles from "./styles.module.scss"
import Loader from "components/loader"
import { useTranslation } from "react-i18next"
import { useMember } from "../../queries"
import { useToSubscribeActivities } from "./queries"
import { useMemberId } from "pages/member"
import { useSearchParams } from "react-router-dom"
import Search from "antd/es/input/Search"
import useOptions from "utils/use-options"
import { useExtSessions } from "../list/queries"
import ActivityItem from "./activityItem"
import { useEffect } from "react"
import { useState } from "react"
import { ReactComponent as FilterIcon } from "assets/icons/filter.svg"
import type { MenuProps } from "antd"

const { Title } = Typography
const { Main } = Layout
export const SESSION_PARAM = "session"

const Registration: React.FC = () => {
  const memberId = useMemberId()
  const member = useMember(memberId)
  const { t } = useTranslation()
  const [priceFilter, setPriceFilter] = useState<string | null>(null)
  const [subscriptionFilter, setSubscriptionFilter] = useState<boolean | null>(
    null
  )
  const [memberOnlyFilter, setMemberOnlyFilter] = useState<boolean | null>(null)

  const sessions = useExtSessions(
    {
      filter: {
        status: { values: ["published", "progress"], operator: "in" },
      },
    },
    {
      select(data) {
        return data.filter((session) =>
          ["published", "progress"].includes(session.status)
        )
      },
    }
  )

  const [searchParams, setSearchParams] = useSearchParams()

  const { data, isLoading } = useToSubscribeActivities(
    memberId!,
    searchParams.get(SESSION_PARAM)
      ? {
          session_id: searchParams.get(SESSION_PARAM),
        }
      : undefined,
    {
      enabled: !!sessions.data && sessions.data.length > 0,
    }
  )

  const options = useOptions(data)

  // Get unique price options
  const uniquePrices = [
    ...new Set(data?.map((item) => item.activity_unit_price_offer)),
  ].sort((a, b) => Number(a) - Number(b))

  // Modify the options to include the new filters
  const filteredOptions = options.options?.filter((activity) => {
    if (priceFilter && activity.activity_unit_price_offer !== priceFilter) {
      return false
    }
    if (
      subscriptionFilter !== null &&
      activity.subscription !== subscriptionFilter
    ) {
      return false
    }
    if (
      memberOnlyFilter !== null &&
      activity.member_only !== memberOnlyFilter
    ) {
      return false
    }
    return true
  })

  const handleSessionFilter = (value: string) => {
    searchParams.set(SESSION_PARAM, value)
    setSearchParams(searchParams, { replace: true })
  }

  const menuItems: MenuProps["items"] = [
    {
      key: "price",
      type: "group",
      label: t("global.price"),
      children: [
        {
          key: "price-select",
          label: (
            <Select
              style={{ width: "200px" }}
              placeholder={t("global.price")}
              allowClear
              value={priceFilter}
              onChange={(value) => setPriceFilter(value ?? null)}
              options={uniquePrices.map((price) => ({
                value: price,
                label: price,
              }))}
              onClick={(e) => e.stopPropagation()}
            />
          ),
        },
      ],
    },
    {
      type: "divider",
    },
    {
      key: "member_only",
      type: "group",
      label: t("events.members_only"),
      children: [
        {
          key: "member-select",
          label: (
            <Select
              style={{ width: "200px" }}
              placeholder={t("events.members_only")}
              allowClear
              value={memberOnlyFilter}
              onChange={(value) => setMemberOnlyFilter(value ?? null)}
              options={[
                { value: true, label: t("global.yes") },
                { value: false, label: t("global.no") },
              ]}
              onClick={(e) => e.stopPropagation()}
            />
          ),
        },
      ],
    },
    {
      type: "divider",
    },
    {
      key: "subscription",
      type: "group",
      label: t("events.subscription_required"),
      children: [
        {
          key: "subscription-select",
          label: (
            <Select
              style={{ width: "200px" }}
              placeholder={t("events.subscription_required")}
              allowClear
              value={subscriptionFilter}
              onChange={(value) => setSubscriptionFilter(value ?? null)}
              options={[
                { value: true, label: t("global.yes") },
                { value: false, label: t("global.no") },
              ]}
              onClick={(e) => e.stopPropagation()}
            />
          ),
        },
      ],
    },
  ]

  useEffect(() => {
    if (sessions.data?.length === 0) {
      searchParams.delete(SESSION_PARAM)
      setSearchParams(searchParams, { replace: true })
      toast.error({
        message: t("errors.error"),
        description: t("events.not_found_session"),
        placement: "top",
      })
    } else if (!searchParams.has(SESSION_PARAM) && sessions.data) {
      const hasPublishedSession = sessions.data.some(
        (session) => session.status === "published"
      )
      const filteredSession = hasPublishedSession
        ? sessions.data.find((session) => session.status === "published")
        : sessions.data.find((session) => session.status === "progress")
      filteredSession && handleSessionFilter(filteredSession.id)
    }
  }, [sessions.data, searchParams, setSearchParams])

  return (
    <>
      <Main.PageHeader
        breadcrumbs={[
          {
            title: member.data?.name,
            link: `/members/${member.data?.id}/events`,
          },
          {
            title: t("events.presubscriptions"),
          },
        ]}
        title={<Title level={3}> {t("events.presubscriptions")} </Title>}
        rightContent={
          <Space>
            <Select
              value={
                !sessions.isLoading ? searchParams.get(SESSION_PARAM) : null
              }
              style={{ width: "100%" }}
              placeholder={t("global.select", {
                entity: t("events.session"),
                context: "female",
              })}
              loading={sessions.isLoading}
              onChange={handleSessionFilter}
              options={sessions.data?.map((session) => ({
                value: session.id,
                label: session.name,
              }))}
            />
            <Search
              value={options.search}
              onChange={(e) => {
                options.setSearch(e.target.value)
              }}
              placeholder={t("global.search")}
              style={{ width: "100%" }}
            />
            <Dropdown
              menu={{ items: menuItems }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button type="text">
                <Badge
                  dot={[priceFilter, subscriptionFilter, memberOnlyFilter].some(
                    (f) => f !== null
                  )}
                >
                  <FilterIcon />
                </Badge>
              </Button>
            </Dropdown>
          </Space>
        }
      />
      {(isLoading && sessions.data && sessions.data.length > 0) ||
      sessions.isLoading ? (
        <Loader />
      ) : (
        <>
          {(sessions.data && sessions.data.length === 0) ||
          (filteredOptions && filteredOptions.length === 0) ? (
            <div className={styles.emptyDataContainer}>
              <Empty />
            </div>
          ) : (
            <div className={styles.cardsContainer}>
              {filteredOptions?.map((activity) => (
                <ActivityItem
                  key={activity.id}
                  activity={activity}
                  member={member.data}
                />
              ))}
            </div>
          )}
        </>
      )}
    </>
  )
}

export default Registration
